import React from 'react';
import { Clock, MapPin, Check, AlertCircle, Key, FileText, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const OnTheDay = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Collection Day Checklist | What to Bring & Expect" rawTitle url="/on-the-day"
        structuredData={generateBreadcrumbSchema([{ name: 'On the Day', path: '/on-the-day' }])}
        description="Everything you need on motorhome collection day at Millie's in Wattisham, Suffolk - documents to bring, 3pm collection and 11am return, fuel rules, free parking and the handover walk-through." />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">On The Day Of Your Hire</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Everything you need to know about collecting your motorhome and what to expect on the day
          </p>
        </div>
      </section>

      {/* Collection Details */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <Alert className="mb-8 border-blue-200 bg-blue-50">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            <AlertDescription className="text-slate-700 font-medium">
              Please arrive 15 minutes before your scheduled collection time to allow for the handover process.
            </AlertDescription>
          </Alert>

          <div className="space-y-8">
            {/* Collection Time */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 rounded-full p-3 flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">Collection & Return Times</h2>
                    <div className="space-y-4 text-slate-700">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-green-200">
                          <p className="text-sm text-slate-600 mb-1">Standard FREE Collection</p>
                          <p className="text-3xl font-bold text-green-600">3:00 PM</p>
                          <p className="text-sm text-slate-500">On your hire start date</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                          <p className="text-sm text-slate-600 mb-1">Standard FREE Drop-off</p>
                          <p className="text-3xl font-bold text-amber-600">11:00 AM</p>
                          <p className="text-sm text-slate-500">On your hire end date</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-purple-200">
                        <p className="font-semibold text-purple-700 mb-1">Need Different Times?</p>
                        <p className="text-sm text-slate-600">
                          Collection and drop-off times can be extended at an additional cost. Select your preferred times via the <strong>extras screen</strong> during the booking process, or contact us to discuss your requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 rounded-full p-3 flex-shrink-0">
                    <MapPin className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">Collection &amp; Drop-off Address</h2>
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-slate-900">Millie's Motorhome Hire</p>
                        <p className="text-slate-700">Clay Hill Farm</p>
                        <p className="text-slate-700">Clay Hill Lane</p>
                        <p className="text-slate-700">Wattisham, Ipswich, Suffolk</p>
                        <p className="text-3xl font-extrabold text-blue-700 tracking-wide">IP7 7JS</p>
                      </div>
                      <div className="pt-4">
                        <a 
                          href="https://www.google.com/maps/search/?api=1&query=Clay+Hill+Farm+Clay+Hill+Lane+Wattisham+Suffolk+IP7+7JS" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Get Directions
                        </a>
                      </div>
                      <p className="text-sm text-slate-600 mt-4">
                        Free secure parking available for your vehicle during your hire period.
                      </p>
                      
                      {/* Car Parking Highlight */}
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 rounded-full p-2">
                            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-800 text-sm">Leave Your Car With Us - FREE!</h4>
                            <p className="text-xs text-green-700">Your car stays safe on our premises while you explore!</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents Required */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 rounded-full p-3 flex-shrink-0">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">Documents to Bring</h2>
                    <ul className="space-y-3">
                      {[
                        'Valid UK driving licence (photocard)',
                        'DVLA check code (generate online at gov.uk)',
                        'Photographic ID (passport or driving licence)',
                        'Proof of address (dated within last 3 months)',
                        'Payment card for security deposit'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Alert className="mt-6 border-yellow-200 bg-yellow-50">
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                      <AlertDescription className="text-slate-700">
                        <strong>Important:</strong> Without these documents, we will not be able to release the motorhome to you.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Handover Process */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 rounded-full p-3 flex-shrink-0">
                    <Key className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">The Handover Process</h2>
                    <p className="text-slate-700 mb-4">
                      Our comprehensive handover typically takes 60-90 minutes. We'll cover everything you need to know:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Vehicle Operation</h3>
                        <ul className="space-y-1 text-sm text-slate-700">
                          <li>• Driving the motorhome</li>
                          <li>• Dashboard controls and displays</li>
                          <li>• Lights, indicators, and wipers</li>
                          <li>• Heating and air conditioning</li>
                          <li>• Reversing camera and sensors</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Living Area</h3>
                        <ul className="space-y-1 text-sm text-slate-700">
                          <li>• Kitchen appliances</li>
                          <li>• Water system and plumbing</li>
                          <li>• Toilet and waste disposal</li>
                          <li>• Heating and hot water</li>
                          <li>• Bed configurations</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">External Features</h3>
                        <ul className="space-y-1 text-sm text-slate-700">
                          <li>• Awning operation</li>
                          <li>• External power hook-up</li>
                          <li>• Gas bottle location and usage</li>
                          <li>• Fresh and waste water</li>
                          <li>• Storage compartments</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Safety & Procedures</h3>
                        <ul className="space-y-1 text-sm text-slate-700">
                          <li>• Fire safety equipment</li>
                          <li>• First aid kit location</li>
                          <li>• Emergency contacts</li>
                          <li>• Breakdown procedure</li>
                          <li>• Insurance details</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pets */}
            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-600 rounded-full p-3 flex-shrink-0">
                    <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">Bringing Your Pet?</h2>
                    <p className="text-slate-700 mb-4">
                      Well-behaved pets are welcome on all our motorhomes! If you're bringing furry friends, here's what you need to know:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                        <p className="text-sm text-slate-600 mb-1">1 Pet</p>
                        <p className="text-3xl font-bold text-amber-600">£20</p>
                        <p className="text-xs text-slate-500">cleaning fee per hire</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                        <p className="text-sm text-slate-600 mb-1">2 Pets (max)</p>
                        <p className="text-3xl font-bold text-amber-600">£40</p>
                        <p className="text-xs text-slate-500">cleaning fee per hire</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-slate-700">
                      <li className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Select 1 or 2 pets via the <strong>extras screen</strong> during booking</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Dogs and cats welcome</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Please bring pet bedding and bowls</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What to Bring */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">What to Bring With You</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Provided by Us</h3>
                    <ul className="space-y-2 text-slate-700 text-sm">
                      {[
                        'All kitchen equipment',
                        'Toilet chemicals',
                        'Cleaning supplies',
                        'Full diesel & AdBlue (must return full)',
                        'Gas bottles'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-semibold text-amber-800 text-sm mb-2">Optional Extras (Not Included)</h4>
                      <ul className="space-y-1 text-xs text-amber-700">
                        <li>• Bedding set: £30 per set (quilt, pillow, towel)</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Bring Your Own</h3>
                    <ul className="space-y-2 text-slate-700 text-sm">
                      {[
                        'Personal clothing and toiletries',
                        'Bedding & towels (or add as £30/set extra)',
                        'Food and drinks',
                        'Entertainment (books, games, etc.)',
                        'Phone chargers',
                        'Maps or sat nav',
                        'Camera for memories!'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-blue-600">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pre-Trip Checklist */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Pre-Trip Checklist</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Check you have all required documents',
                    'Generate your DVLA check code online',
                    'Plan your route to our location',
                    'Ensure payment card has sufficient funds for deposit',
                    'Confirm your collection time with us',
                    'Pack light - space is limited!',
                    'Check weather forecast',
                    'Download campsite information'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Questions About Your Collection?</h2>
          <p className="text-slate-600 mb-8">
            If you have any questions or concerns before your collection day, please don't hesitate to contact us.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="mailto:milliesmotorhomehire@gmail.com"
              className="bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold"
            >
              Email Us
            </a>
            <a
              href="tel:07527006966"
              className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-md hover:bg-blue-50 transition-colors font-semibold"
            >
              Call: 07527 006966
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnTheDay;
